import React, { useContext, useEffect, useState } from "react";
import PagePreview from "./Preview";
import { useSelector } from "react-redux";
import { FullGridContext } from "..";
import { getData } from "~/functions/gridFunctions";
import PageContainer from "~/components/PageContainer";
import { format, parseISO, set } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import Attendance from "./Attendance";
import AttendanceReport from "./Attendance Report";
import ModalFilters, { getStoredFilters } from "./ModalFilters";
import { Pin } from "lucide-react";

const GRID_HEADER_NORMAL = [
  { title: "Name", name: "name", type: "text", filter: false },
  { title: "Status", name: "status", type: "text", filter: true },
  { title: "Level", name: ["level", "name"], type: "text", filter: true },
  { title: "Classroom", name: "classroom_name", type: "text", filter: false },
  { title: "Workload", name: "workload_name", type: "text", filter: false },
  { title: "Teacher", name: "staff_name", type: "text", filter: false },
  { title: "In Group", name: "students", type: "integer", filter: false },
  { title: "Start Date", name: "start_date", type: "date", filter: false },
  { title: "End Date", name: "end_date", type: "date", filter: false },
];

const GRID_HEADER_CAPACITY = [
  { title: "Name", name: "name", type: "text", filter: false },
  { title: "Status", name: "status", type: "text", filter: true },
  { title: "In Group", name: "total_in_group", type: "integer", filter: false },
  { title: "Vacation", name: "vacation", type: "tooltip", tooltipKey: "students_on_vacation", filter: false },
  { title: "M.E.", name: "medical_excuse", type: "tooltip", tooltipKey: "students_on_medical_excuse", filter: false },
  { title: "Active", name: "active", type: "integer", filter: false },
  { title: "Capacity", name: "capacity", type: "integer", filter: false },
  { title: "Availability", name: "availability", type: "integer", filter: false },
  { title: "% Occup", name: "percent_occup", type: "text", filter: false },
];

function mapNormalRow({ id, name, status, classes, level, classroom, workload, staff, capacity_data, start_date, end_date, canceled_at }, index, limit) {
  const cd = capacity_data || {};
  return {
    show: true,
    id,
    fields: [
      name,
      status,
      level?.name,
      classroom?.class_number,
      workload?.name,
      staff?.name,
      cd.total_in_group ?? 0,
      start_date ? format(parseISO(start_date), "MM/dd/yyyy") : "",
      end_date ? format(parseISO(end_date), "MM/dd/yyyy") : "",
    ],
    others: { classes: classes?.length ?? 0 },
    selectable: true,
    canceled: canceled_at,
    page: Math.ceil((index + 1) / limit),
  };
}

function mapCapacityRow({ id, name, status, classes, capacity_data, canceled_at }, index, limit) {
  const cd = capacity_data || {};
  return {
    show: true,
    id,
    fields: [
      name,
      status,
      cd.total_in_group ?? 0,
      cd.vacation ?? 0,
      cd.medical_excuse ?? 0,
      cd.active ?? 0,
      cd.capacity ?? 0,
      cd.availability ?? 0,
      cd.percent_occup != null ? `${cd.percent_occup}%` : "—",
    ],
    others: {
      classes: classes?.length ?? 0,
      students_on_vacation: cd.students_on_vacation || [],
      students_on_medical_excuse: cd.students_on_medical_excuse || [],
    },
    selectable: true,
    canceled: canceled_at,
    page: Math.ceil((index + 1) / limit),
  };
}

export default function Studentgroups() {
  const filial = useSelector((state) => state.auth.filial);
  const [loading, setLoading] = useState(false);
  const defaultOrderBy = { column: "name", asc: true };
  const [capacityView, setCapacityView] = useState(false);
  const [selected, setSelected] = useState([]);
  const [startGroup, setStartGroup] = useState(false);
  const [openAttendance, setOpenAttendance] = useState(false);
  const [openAttendanceReport, setOpenAttendanceReport] = useState(false);
  const [openFiltersModal, setOpenFiltersModal] = useState(false);
  const [filtersPinned, setFiltersPinned] = useState(() => localStorage.getItem("studentgroup_filters_pinned") === "true");

  const defaultGridHeader = capacityView ? GRID_HEADER_CAPACITY : GRID_HEADER_NORMAL;

  const {
    accessModule,
    activeFilters,
    gridData,
    gridDetails,
    gridHeader,
    handleFilters,
    handleOpened,
    limit,
    loadingData,
    opened,
    orderBy,
    page,
    pages,
    search,
    setActiveFilters,
    setGridData,
    setGridDetails,
    setGridHeader,
    setLimit,
    setLoadingData,
    setOpened,
    setOrderBy,
    setPage,
    setPages,
    setSearch,
    setTotalRows,
    setSuccessfullyUpdated,
    successfullyUpdated,
    totalRows,
  } = useContext(FullGridContext);

  const pageAccess = accessModule.children.find(
    (el) => el.alias === "studentgroups"
  );

  async function handleStart() {
    setLoading(true);
    if (selected[0].fields[1] !== "In Formation") {
      toast("Only in formation groups can be started!", {
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }
    try {
      await api.post(`/studentgroups/start/${selected[0].id}`);
      toast("Group started!", { autoClose: 3000 });
      setLoading(false);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
      setLoading(false);
    }
    setSelected([]);
    handleOpened(null);
  }

  async function handlePause() {
    setLoading(true);
    if (selected[0].fields[1] !== "Ongoing") {
      toast("Only ongoing groups can be paused!", {
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }
    try {
      await api.post(`/studentgroups/pause/${selected[0].id}`);
      toast("Group paused!", { autoClose: 3000 });
      setLoading(false);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
      setLoading(false);
    }
    setSelected([]);
    handleOpened(null);
  }

  function handleAttendance() {
    setOpenAttendance(!openAttendance);
    handleOpened(null);
  }

  function handleAttendanceReport() {
    setOpenAttendanceReport(!openAttendanceReport);
    handleOpened(null);
  }

  async function handleApplyFilters(queryString) {
    setLoadingData(true);
    try {
      const { data: res } = await api.get(queryString);
      const rows = res?.rows || res || [];
      const pagesCalc = Math.ceil((res?.totalRows || rows.length) / limit) || 1;
      setPages(pagesCalc);
      setGridDetails({ totalRows: res?.totalRows || rows.length, pages: pagesCalc });
      setGridData(rows.map((row, index) =>
        capacityView ? mapCapacityRow(row, index, limit) : mapNormalRow(row, index, limit)
      ));
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } finally {
      setLoadingData(false);
    }
  }

  async function loader() {
    setLoadingData(true);
    const stored = getStoredFilters();
    const extraParams = { capacity_view: true };
    if (stored) {
      if (stored.level) extraParams.level_id = stored.level;
      if (stored.status) extraParams.status = stored.status;
      if (stored.workload) extraParams.workload_id = stored.workload;
      if (stored.teacher) extraParams.staff_id = stored.teacher;
      if (stored.startDateFrom) extraParams.start_date_from = stored.startDateFrom;
      if (stored.startDateTo) extraParams.start_date_to = stored.startDateTo;
      if (stored.endDateFrom) extraParams.end_date_from = stored.endDateFrom;
      if (stored.endDateTo) extraParams.end_date_to = stored.endDateTo;
    }
    const data = await getData("studentgroups", {
      limit,
      page,
      orderBy,
      setPages,
      setGridData,
      search,
      defaultGridHeader: capacityView ? GRID_HEADER_CAPACITY : GRID_HEADER_NORMAL,
      defaultOrderBy,
      setGridDetails,
      extraParams,
    });

    if (!data) {
      return;
    }
    setGridData(data.map((row, index) =>
      capacityView ? mapCapacityRow(row, index, limit) : mapNormalRow(row, index, limit)
    ));
    setLoadingData(false);
  }

  useEffect(() => {
    if (selected.length === 0) {
      loader();
    }
  }, [opened, filial, orderBy, search, limit, selected, page, capacityView]);

  const selectionFunctions = [];

  // Função liberada para professor
  if (
    selected.length > 0 &&
    (selected[0].fields[1] === "Ongoing" ||
      selected[0].fields[1] === "Finished")
  ) {
    selectionFunctions.push({
      title: "Attendance",
      alias: "attendance",
      fun: handleAttendance,
      icon: "Highlighter",
      Page: Attendance,
      opened: openAttendance,
      setOpened: setOpenAttendance,
      selected,
    });
    selectionFunctions.push({
      title: "Attendance Report",
      alias: "attendance-report",
      fun: handleAttendanceReport,
      icon: "Highlighter",
      Page: AttendanceReport,
      opened: openAttendanceReport,
      setOpened: setOpenAttendanceReport,
      selected,
    });
  }

  // Funções para não professores
  // } else {
  if (selected.length === 0 || selected[0].fields[1] === "In Formation") {
    selectionFunctions.push({
      title: "Start Group",
      alias: "start-group",
      fun: handleStart,
      icon: "Play",
      Page: () => null,
      opened: startGroup,
      setOpened: setStartGroup,
      selected,
    });
  }
  if (selected.length > 0 && selected[0].fields[1] === "Ongoing") {
    if (selected[0].others.classes === 0) {
      selectionFunctions.push({
        title: "Pause Group",
        alias: "pause-group",
        fun: handlePause,
        icon: "Pause",
        Page: () => null,
        opened: startGroup,
        setOpened: setStartGroup,
        selected,
      });
    }
  }
  // }

  return (
    <>
      <PageContainer
        FullGridContext={FullGridContext}
        PagePreview={PagePreview}
        pageAccess={pageAccess}
        defaultGridHeader={defaultGridHeader}
        selection={{
          multiple: false,
          selected,
          setSelected,
          functions: selectionFunctions,
        }}
        filtersModal={{
          onOpen: () => setOpenFiltersModal(true),
          viewSelect: (
            <div className="flex items-center gap-2">
              {filtersPinned && (
                <div className="flex items-center gap-1 text-orange-500 text-xs font-medium">
                  <Pin size={13} className="fill-orange-500" />
                  Filters Applied
                </div>
              )}
              <select
                value={capacityView ? "capacity" : "normal"}
                onChange={(e) => {
                  const isCapacity = e.target.value === "capacity";
                  setCapacityView(isCapacity);
                  setGridHeader(isCapacity ? GRID_HEADER_CAPACITY : GRID_HEADER_NORMAL);
                }}
                className="border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-sky-600 bg-white h-8"
              >
                <option value="normal">Normal</option>
                <option value="capacity">Capacity</option>
              </select>
            </div>
          ),
        }}
      />
      <ModalFilters
        opened={openFiltersModal}
        onClose={() => {
          setOpenFiltersModal(false);
          setFiltersPinned(localStorage.getItem("studentgroup_filters_pinned") === "true");
        }}
        onApplyFilters={handleApplyFilters}
        capacityView={capacityView}
      />
      {loading && (
        <div className="flex justify-center items-center h-screen absolute top-0 left-0 w-full bg-gray-500 bg-opacity-50">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-700" />
        </div>
      )}
    </>
  );
}
